import React from 'react';
import { Pedido } from '../../../../types';
import { FaClock, FaCheck, FaTimes } from 'react-icons/fa';
import { MdDeliveryDining } from 'react-icons/md';
import { FaStore } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import './PedidoCard.scss';

interface PedidoCardProps {
  pedido: Pedido;
  onSelect: () => void;
  isSelected: boolean;
  onMarkAsDelivered?: (pedido_id: number) => void;
  onCancel?: (pedido_id: number) => void;
}

const PedidoCard: React.FC<PedidoCardProps> = ({ 
  pedido, 
  onSelect, 
  isSelected,
  onMarkAsDelivered,
  onCancel
}) => {
  const formatHoraEntrega = (hora_entrega: string | null) => {
    if (!hora_entrega) return null;
    const date = new Date(hora_entrega);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const renderProviderLogo = (id: number) => {
    const providerFilenameMap: Record<number, string> = {
      1: 'uber-eats.png',
      2: 'rappi.png',
      3: 'pedidos-ya.png',
      4: 'JUSTO.png',
      7: 'didi-food.jpg',
    };
    const filename = providerFilenameMap[id] || 'logo.png';
    return (
      <div className="hoster-pedido-card__provider-logo">
        <img 
          alt="provider logo"
          className="provider-logo-img"
          src={`${process.env.PUBLIC_URL}/icons/providers/${filename}`} 
        />
      </div>
    );
  };

  const getPedidoTypeIcon = () => {
    // pedido_type_id: 3 = Both Delivery and Pickup orders
    if (pedido.pedido_type_id === 3) {
      // Check if it has a third party provider (Delivery) or not (Pickup)
      if (pedido.third_party_provider_id) {
        return <MdDeliveryDining className="hoster-pedido-card__icon" />;
      } else {
        return <FaStore className="hoster-pedido-card__icon" />;
      }
    }
    return null;
  };

  const getStatusBadge = () => {
    if (pedido.isDelivered) {
      return <span className="hoster-pedido-badge hoster-pedido-badge--delivered">Entregado</span>;
    } else if (pedido.payed) {
      return <span className="hoster-pedido-badge hoster-pedido-badge--paid">Pagado</span>;
    } else if (pedido.isReady) {
      return <span className="hoster-pedido-badge hoster-pedido-badge--ready">Listo</span>;
    } else if (pedido.isStarted) {
      return <span className="hoster-pedido-badge hoster-pedido-badge--cooking">En cocina</span>;
    }
    return <span className="hoster-pedido-badge hoster-pedido-badge--pending">Pendiente</span>;
  };

  // Calculate total price from selections
  const getTotalPrice = () => {
    if (!pedido.selections || pedido.selections.length === 0) return 0;
    return pedido.selections.reduce((total, selection) => total + (selection.total_price || 0), 0);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div
      className={`hoster-pedido-card ${isSelected ? 'hoster-pedido-card--selected' : ''}`}
      onClick={onSelect}
    >
      {/* Header */}
      <div className="hoster-pedido-card__header">
        <div className="hoster-pedido-card__title">
          {getPedidoTypeIcon()}
          <span className="hoster-pedido-card__type">
            {pedido.client_name || 'Cliente sin nombre'}
          </span>
        </div>
        <div className="hoster-pedido-card__header-right">
          {pedido.third_party_provider_id && renderProviderLogo(pedido.third_party_provider_id)}
          <div className="hoster-pedido-card__number">#{pedido.pedido_id}</div>
        </div>
      </div>

      {/* Status and Price Row */}
      <div className="hoster-pedido-card__status-row">
        {getStatusBadge()}
        <div className="hoster-pedido-card__price-badge">
          {formatPrice(getTotalPrice())}
        </div>
      </div>

      {/* Delivery Time Info */}
      <div className="hoster-pedido-card__info">
        {pedido.hora_entrega && (
          <div className="hoster-pedido-card__info-item">
            <FaClock className="hoster-pedido-card__info-icon" />
            <span className="hoster-pedido-card__info-text">
              {formatHoraEntrega(pedido.hora_entrega)}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!pedido.isDelivered && (
        <div className="hoster-pedido-card__actions" onClick={(e) => e.stopPropagation()}>
          {pedido.payed && onMarkAsDelivered && (
            <Button
              variant="none"
              size="sm"
              className="hoster-pedido-card__btn hoster-pedido-card__btn--delivered"
              onClick={() => onMarkAsDelivered(pedido.pedido_id)}
            >
              <FaCheck /> Entregado
            </Button>
          )}
          {!pedido.payed && onCancel && (
            <Button
              variant="none"
              size="sm"
              className="hoster-pedido-card__btn hoster-pedido-card__btn--cancel"
              onClick={() => onCancel(pedido.pedido_id)}
            >
              <FaTimes /> Cancelar
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PedidoCard;
